import {
  Modal,
  useSession,
  useTranslation,
  ButtonAlignment,
  ButtonSize,
  ButtonStyles,
  Theme,
  Button,
  useNextgenRouter,
} from '@nextgen-web-framework/all';
import {
  type Cart,
  updateCart,
  CartEntryDetails,
  AmwayUserDetails,
  InitialCartState,
  PublishEvent,
  CartEventsEnum,
  getActiveCart,
  cartApi,
  CartConstantsEnum,
  isGroupOrder,
  SubCartDetail,
} from '@commerce-cart-webcomponents/sdk';
import { useDispatch, useSelector } from 'react-redux';
import { StorageService } from '@nextgen-web-framework/core';
import type { AppState } from '@nextgen-web-framework/core';
import { useEffect, useState } from 'react';
import { CheckoutCardForModal } from '../../../common/components/checkout-card-for-modal/CheckoutCardForModal';
import { useExceptionToast, useFeatureFlag } from '../../../common';
import styles from './ExpiryModal.module.scss';

export type ExpiryModalProps = {
  data: Cart;
};
export type CartEntryDetailsType = {
  amwayUser?: AmwayUserDetails;
  subCartId: string;
  entries: CartEntryDetails[];
};

export const ExpiryModal = ({ data }: ExpiryModalProps) => {
  const t = useTranslation();
  const router = useNextgenRouter();
  const dispatch = useDispatch();
  const { isABO } = useSession();
  const triggerToast = useExceptionToast();
  const commerceCartState = useSelector((state: AppState) => state.commerceCart);
  const { cart }: InitialCartState = commerceCartState;
  const cartId = router.params?.cartId ?? '';
  const currentActiveCart = getActiveCart(cart, cartId as string);

  const primaryLabelText = t('cart.expiryModal.primaryLabel');
  const secondaryLabelText = t('cart.expiryModal.secondaryLabel');
  const headLineText = t('cart.expiryModal.headLine');
  const headingText = t('cart.expiryModal.heading');
  const localstorage = StorageService.getLocalStorage();
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const { hidePromoUnavailablePopupFlag } = useFeatureFlag();

  const [allExpiredPromotionsEntries, setAllExpiredPromotionsEntries] = useState<CartEntryDetailsType[]>([]);

  const allExpiredPromotions: CartEntryDetailsType[] = [];
  const allExpiredPromotionsForNonCatalogueItems: CartEntryDetailsType[] = [];

  const updateCartEntries = async (expiredPromotions: CartEntryDetailsType[], deleteProduct = false) => {
    const updatedExpiredProduct = expiredPromotions.map(({ subCartId, entries }) => ({
      subCartId,
      entries: entries.map(({ entryId, product, partnerPromotionId, quantity }) => ({
        entryId,
        product: {
          id: product.id,
        },
        partnerPromotionId,
        quantity: deleteProduct ? 0 : quantity,
      })),
    }));
    try {
      const result = await cartApi.updateCartEntries({
        cartId: localstorage.retrieve(CartConstantsEnum.CartId),
        subCartId: currentActiveCart.subCartId,
        payload: {
          subCarts: updatedExpiredProduct,
          version: Number(localstorage.retrieve(CartConstantsEnum.CartReqVersion)),
        },
      });
      setShowExpiryModal(false);
      dispatch(updateCart(result as Cart));
      PublishEvent({
        cartEvent: CartEventsEnum.UpdateMiniCartIcon,
        cart: result as Cart,
      });
    } catch (error) {
      triggerToast();
    }

    return false;
  };

  useEffect(() => {
    data?.subCarts.map((subcart: SubCartDetail) => {
      const expiredPromotions = subcart.entries?.filter(
        (entry: CartEntryDetails) => entry.isOrphanedPromotionalEntry && (entry.product.isSellable as boolean),
      );
      const expiredPromotionsForNonCatalogueItems = subcart.entries?.filter(
        (entry: CartEntryDetails) => entry.isOrphanedPromotionalEntry && !entry.product.isSellable,
      );

      expiredPromotionsForNonCatalogueItems?.length &&
        allExpiredPromotionsForNonCatalogueItems.push({
          subCartId: subcart.subCartId ?? data.id,
          entries: expiredPromotionsForNonCatalogueItems,
        });

      expiredPromotions?.length &&
        allExpiredPromotions.push({
          amwayUser: subcart.amwayUser,
          subCartId: subcart.subCartId ?? data.id,
          entries: expiredPromotions,
        });
    });

    if (allExpiredPromotions.length && !currentActiveCart.modificationResult?.isDbUpdateRequired) {
      setAllExpiredPromotionsEntries(allExpiredPromotions);
      //hide Promotion Unavailable pop up for thailand market
      hidePromoUnavailablePopupFlag ? updateCartEntries(allExpiredPromotions, true) : setShowExpiryModal(true);
    }

    if (allExpiredPromotionsForNonCatalogueItems.length && !currentActiveCart.modificationResult?.isDbUpdateRequired) {
      updateCartEntries(allExpiredPromotionsForNonCatalogueItems, true);
    }
  }, [data]);

  return (
    <>
      {showExpiryModal && (
        <Modal
          buttonAlignment={ButtonAlignment.VERTICAL}
          closeIcon={false}
          primaryButtonLabel={''}
          headline={headLineText}
          useReactPortal={true}
          portalContainerId="react-portal-modal"
        >
          <>
            <div className={styles['expiry-modal-container']}>
              <div className={styles['expiry-modal-container-heading']}>{headingText}</div>
              {allExpiredPromotionsEntries?.map((orphanedEntry: CartEntryDetailsType) => (
                <>
                  {isGroupOrder(cart.orderType) && (
                    <div className={styles['expiry-modal-container-name']}>
                      {`${orphanedEntry.amwayUser?.firstName ?? ''} ${orphanedEntry.amwayUser?.lastName ?? ''}`} (
                      {data.amwayUser.amwayUserId === orphanedEntry?.amwayUser?.amwayUserId
                        ? t('cart.gcoList.You')
                        : orphanedEntry.amwayUser?.accountId}
                      )
                    </div>
                  )}
                  {orphanedEntry.entries.map((entry: CartEntryDetails) =>
                    CheckoutCardForModal({
                      entry,
                      isABO: isABO(),
                      className: styles['checkout-card-component'],
                    }),
                  )}
                </>
              ))}
            </div>

            <div className={styles['merge-expiry-button-container']}>
              <Button
                title={primaryLabelText}
                buttonStyle={ButtonStyles.SOLID}
                theme={Theme.BLACK}
                size={ButtonSize.MEDIUM}
                data-testid="keep-in-cart-btn"
                onClick={() => updateCartEntries(allExpiredPromotionsEntries)}
                aria-label={primaryLabelText}
              />

              <Button
                title={secondaryLabelText}
                buttonStyle={ButtonStyles.OUTLINE}
                theme={Theme.BLACK}
                size={ButtonSize.MEDIUM}
                data-testid="remove-item-btn"
                onClick={() => updateCartEntries(allExpiredPromotionsEntries, true)}
                aria-label={secondaryLabelText}
              />
            </div>
          </>
        </Modal>
      )}
    </>
  );
};
